import { createFileRoute } from "@tanstack/react-router";
import { Building2, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { useApiParameters } from "@/hooks/useApiParameters";
import { OrganizationSettingsNavigation } from "@/routes/_auth/app/_app/org/$orgId/settings/-components/OrganizationSettingsNavigation";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/settings/export/",
)({
	loader: async ({ context }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);

		return { userId, token };
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();
	Route.useLoaderData();
	const { orgId } = Route.useParams();
	const navigate = Route.useNavigate();

	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("orgSettings.title")}
				description={t("orgSettings.description")}
				showBackButton={true}
				backButtonText={t("common.back")}
				hideOnMobile={false}
			/>

			<div
				className="flex-1 flex flex-col md:flex-row h-[calc(100vh-4rem)] min-h-0 overflow-hidden"
				data-testid="organization-settings-container"
			>
				<OrganizationSettingsNavigation activeTab="export" />

				<div
					className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto"
					data-testid="organization-settings-main-content-area"
				>
					<Card>
						<CardHeader className="px-3 md:px-6 pt-3 md:pt-6 pb-2 md:pb-4">
							<CardTitle className="flex items-center space-x-2 text-sm md:text-base">
								<Download className="h-4 w-4 md:h-5 md:w-5 text-primary" />
								<span>{t("orgSettings.export")}</span>
							</CardTitle>
							<CardDescription className="text-xs md:text-sm">
								{t("orgSettings.exportDescription")}
							</CardDescription>
						</CardHeader>
						<CardContent className="px-3 md:px-6 pb-3 md:pb-6">
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
								<Card className="hover:shadow-md transition-shadow cursor-pointer">
									<CardHeader className="px-3 md:px-6 pt-3 md:pt-6 pb-2 md:pb-3">
										<CardTitle className="text-sm md:text-lg flex items-center space-x-2">
											<Building2 className="h-4 w-4 md:h-5 md:w-5 text-primary" />
											<span>{t("orgSettings.exportCustomers")}</span>
										</CardTitle>
										<CardDescription className="text-xs md:text-sm">
											{t("orgSettings.exportCustomersDescription")}
										</CardDescription>
									</CardHeader>
									<CardContent className="px-3 md:px-6 pb-3 md:pb-6">
										<Button
											className="w-full btn btn-primary text-sm md:text-base"
											onClick={() => {
												navigate({
													to: "/app/org/$orgId/settings/export/customers",
													params: { orgId },
												});
											}}
										>
											<Download className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1.5 md:mr-2" />
											{t("orgSettings.exportCustomers")}
										</Button>
									</CardContent>
								</Card>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	);
}
